/**
 * MediaPipe FaceLandmarker wrapper + mouth state machine + calibration.
 *
 * All landmark/blendshape coordinates here are in *unmirrored* normalized video
 * space ([0,1] over the raw camera frame). Mirroring and cover-fitting into the
 * game's field space happens in the renderer's mapper, kept deliberately out of
 * this file so the sensor layer stays coordinate-system agnostic.
 */

import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import type { FaceLandmarkerResult, NormalizedLandmark } from "@mediapipe/tasks-vision";

export type Delegate = "GPU" | "CPU";
export type Signal = "jaw" | "lip";

export interface MouthEllipse {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export interface FaceTracker {
  activeDelegate: Delegate;
  readonly landmarks: NormalizedLandmark[] | null;
  readonly jawOpen: number | null;
  readonly hasFace: boolean;
  /** Total count of actual `detectForVideo` calls (gated-out frames don't count). Perf overlay only. */
  readonly detectCount: number;
  /** Rolling average of the last ~20 `detectForVideo` durations, in ms. Perf overlay only. */
  readonly avgInferenceMs: number;
  update(nowMs: number): void;
  lipGapRatio(): number | null;
  mouthEllipseVideoNorm(): MouthEllipse | null;
  /**
   * Release the underlying MediaPipe landmarker (GPU/WASM resources). The
   * main session tracker lives for the app's lifetime and never calls this;
   * it exists for short-lived SECOND instances (Phase 4's penalty stage runs
   * an independent tracker against the remote video — see `createFaceTracker`
   * doc — and must dispose it when the stage tears down).
   */
  close(): void;
}

const WASM_ROOT = "/vendor/mediapipe/wasm";
const MODEL_PATH = "/vendor/mediapipe/face_landmarker.task";

/**
 * Detection throttle escalation ladder (Phase 5 thermal adaptation):
 * skip re-detecting the exact same frame, or faster than the CURRENT level's
 * interval (~22Hz floor at level 0, degrading to ~15Hz then ~10Hz under
 * sustained thermal pressure — see checkThermal()).
 */
const DETECT_INTERVALS_MS = [45, 66, 100] as const;
/** Sustained-high (raise) / sustained-low (recover) windows before stepping the throttle. */
const THERMAL_RAISE_AFTER_MS = 3_000;
const THERMAL_RECOVER_AFTER_MS = 10_000;
const THERMAL_HIGH_MS = 45;
const THERMAL_LOW_MS = 25;

/** FaceLandmarker emits ~478 points (mesh + iris); tolerate minor version drift. */
const EXPECTED_LANDMARK_COUNT = 478;
const LANDMARK_COUNT_TOLERANCE = 50;
const LANDMARK_COORD_MIN = -0.5;
const LANDMARK_COORD_MAX = 1.5;

/** Perf overlay: rolling window size for the inference-duration average. */
const INFERENCE_WINDOW_SIZE = 20;

function dist2d(a: NormalizedLandmark, b: NormalizedLandmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

async function makeLandmarker(
  vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>,
  delegate: Delegate,
): Promise<FaceLandmarker> {
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: MODEL_PATH, delegate },
    runningMode: "VIDEO",
    numFaces: 1,
    outputFaceBlendshapes: true,
  });
}

/**
 * Warmup self-test: run a few detections on the live video with strictly
 * increasing timestamps. GPU delegates can fail loudly (throw) or silently on
 * some devices (documented iOS cases); a throw here forces the CPU fallback.
 *
 * A thrown exception isn't the only failure mode — some devices report a
 * silent GPU misconfiguration by returning structurally garbage output
 * instead of erroring. So we also sanity-check the LAST warmup result: the
 * `faceLandmarks` field must be an array, and if it detected a face, that
 * face's landmark list must be close to the expected ~478 points with all
 * coordinates in a plausible normalized range. A clean "no face in frame"
 * result (empty array) is NOT a failure — only structurally invalid output is.
 */
async function selfTest(landmarker: FaceLandmarker, video: HTMLVideoElement): Promise<boolean> {
  try {
    const base = performance.now();
    let last: FaceLandmarkerResult | undefined;
    for (let i = 0; i < 3; i++) {
      last = landmarker.detectForVideo(video, base + i * 16);
    }
    if (!last || !Array.isArray(last.faceLandmarks)) return false;

    const face = last.faceLandmarks[0];
    if (face) {
      if (Math.abs(face.length - EXPECTED_LANDMARK_COUNT) > LANDMARK_COUNT_TOLERANCE) return false;
      for (const lm of face) {
        if (
          !Number.isFinite(lm.x) ||
          !Number.isFinite(lm.y) ||
          lm.x < LANDMARK_COORD_MIN ||
          lm.x > LANDMARK_COORD_MAX ||
          lm.y < LANDMARK_COORD_MIN ||
          lm.y > LANDMARK_COORD_MAX
        ) {
          return false;
        }
      }
    }
    return true;
  } catch (err) {
    console.warn("[face] delegate self-test failed:", err);
    return false;
  }
}

/**
 * The shared "create a tracker" helper — reusable for MULTIPLE independent
 * instances, each bound to its own `<video>`. The main session calls this
 * once for the local camera; Phase 4's penalty stage calls it again (lazily,
 * at penalty start) for a SECOND instance pointed at the opponent's remote
 * video, so the GPU/CPU self-test + fallback logic is never duplicated.
 * Callers of a short-lived instance must call `close()` when done with it.
 */
export async function createFaceTracker(video: HTMLVideoElement): Promise<FaceTracker> {
  const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);

  let delegate: Delegate = "GPU";
  let landmarker = await makeLandmarker(vision, delegate);

  if (!(await selfTest(landmarker, video))) {
    landmarker.close();
    delegate = "CPU";
    landmarker = await makeLandmarker(vision, delegate);
    // Best-effort CPU warmup; failure here is non-fatal (we still run with it).
    await selfTest(landmarker, video);
  }

  console.info(`[face] activeDelegate = ${delegate}`);
  return new FaceTrackerImpl(video, landmarker, delegate);
}

class FaceTrackerImpl implements FaceTracker {
  landmarks: NormalizedLandmark[] | null = null;
  jawOpen: number | null = null;
  hasFace = false;
  detectCount = 0;

  private lastVideoTime = -1;
  private lastDetect = -Infinity;
  private readonly inferenceTimes: number[] = [];
  /** Index into DETECT_INTERVALS_MS — 0 = full rate, higher = more throttled. See checkThermal(). */
  private throttleLevel = 0;
  private highSinceMs: number | null = null;
  private lowSinceMs: number | null = null;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly landmarker: FaceLandmarker,
    public activeDelegate: Delegate,
  ) {}

  get avgInferenceMs(): number {
    if (this.inferenceTimes.length === 0) return 0;
    let sum = 0;
    for (const t of this.inferenceTimes) sum += t;
    return sum / this.inferenceTimes.length;
  }

  private currentIntervalMs(): number {
    return DETECT_INTERVALS_MS[this.throttleLevel] ?? DETECT_INTERVALS_MS[0];
  }

  /**
   * Thermal adaptation: sustained-high inference (>45ms avg) for 3+
   * consecutive seconds steps the detection interval up one level
   * (45→66→100ms, i.e. ~22→15→10Hz floor); sustained-low (<25ms avg) for 10+
   * consecutive seconds recovers one level. Streaks reset the moment the
   * average crosses back out of range — this is a real "consecutive" window,
   * not a cumulative counter. Called once per actual detection (naturally
   * rate-limited by the throttle itself).
   */
  private checkThermal(nowMs: number): void {
    const avg = this.avgInferenceMs;
    if (avg > THERMAL_HIGH_MS) {
      this.lowSinceMs = null;
      if (this.highSinceMs === null) this.highSinceMs = nowMs;
      if (this.throttleLevel < DETECT_INTERVALS_MS.length - 1 && nowMs - this.highSinceMs >= THERMAL_RAISE_AFTER_MS) {
        this.throttleLevel++;
        this.highSinceMs = null;
        console.info(
          `[face] thermal throttle up: avg ${avg.toFixed(1)}ms → level ${this.throttleLevel} ` +
            `(${this.currentIntervalMs()}ms, ~${Math.round(1000 / this.currentIntervalMs())}Hz floor)`,
        );
      }
    } else {
      this.highSinceMs = null;
      if (avg < THERMAL_LOW_MS) {
        if (this.lowSinceMs === null) this.lowSinceMs = nowMs;
        if (this.throttleLevel > 0 && nowMs - this.lowSinceMs >= THERMAL_RECOVER_AFTER_MS) {
          this.throttleLevel--;
          this.lowSinceMs = null;
          console.info(
            `[face] thermal throttle recovered: avg ${avg.toFixed(1)}ms → level ${this.throttleLevel} ` +
              `(${this.currentIntervalMs()}ms, ~${Math.round(1000 / this.currentIntervalMs())}Hz floor)`,
          );
        }
      } else {
        this.lowSinceMs = null;
      }
    }
  }

  update(nowMs: number): void {
    // Gate: only re-detect when the video advanced to a new frame AND enough
    // wall time has passed (throttle inference for thermals / battery — the
    // interval itself adapts, see checkThermal()).
    if (this.video.currentTime === this.lastVideoTime) return;
    if (nowMs - this.lastDetect < this.currentIntervalMs()) return;
    this.lastVideoTime = this.video.currentTime;
    this.lastDetect = nowMs;

    let result: FaceLandmarkerResult;
    const t0 = performance.now();
    try {
      result = this.landmarker.detectForVideo(this.video, nowMs);
    } catch (err) {
      console.warn("[face] detectForVideo threw:", err);
      return;
    }
    this.detectCount++;
    this.inferenceTimes.push(performance.now() - t0);
    if (this.inferenceTimes.length > INFERENCE_WINDOW_SIZE) this.inferenceTimes.shift();
    this.checkThermal(nowMs);

    const face = result.faceLandmarks[0];
    if (face && face.length > 0) {
      this.landmarks = face;
      this.hasFace = true;
      const blend = result.faceBlendshapes[0];
      if (blend) {
        // Look the blendshape up BY NAME, never by numeric index — the order
        // is not part of the model contract and shifts between versions.
        const cat = blend.categories.find((c) => c.categoryName === "jawOpen");
        this.jawOpen = cat ? cat.score : null;
      } else {
        this.jawOpen = null;
      }
    } else {
      // Face lost: keep the last landmarks for ellipse continuity but mark the
      // face gone so callers stop feeding the FSM / disable eating.
      this.hasFace = false;
      this.jawOpen = null;
    }
  }

  lipGapRatio(): number | null {
    const lm = this.landmarks;
    if (!lm) return null;
    const upper = lm[13];
    const lower = lm[14];
    const forehead = lm[10];
    const chin = lm[152];
    if (!upper || !lower || !forehead || !chin) return null;
    const faceHeight = dist2d(forehead, chin);
    if (faceHeight <= 0) return null;
    return dist2d(upper, lower) / faceHeight;
  }

  mouthEllipseVideoNorm(): MouthEllipse | null {
    const lm = this.landmarks;
    if (!lm) return null;
    const upper = lm[13];
    const lower = lm[14];
    const left = lm[61];
    const right = lm[291];
    if (!upper || !lower || !left || !right) return null;
    const cx = (upper.x + lower.x) / 2;
    const cy = (upper.y + lower.y) / 2;
    // 1.2× enlarged (2026-07-03 user feedback: the mouth hitbox felt too
    // small). The server still adds its own 15% RTT expansion on top
    // (MOUTH_HIT_EXPANSION) and clamps rx/ry to 0.15 server-side, so extreme
    // close-ups simply cap there.
    const rx = (dist2d(left, right) / 2) * 1.38;
    const ry = Math.max((dist2d(upper, lower) / 2) * 1.5, rx * 0.35);
    return { cx, cy, rx, ry };
  }

  close(): void {
    this.landmarker.close();
  }
}

/**
 * Two-threshold hysteresis mouth FSM. Requires 2 consecutive confirming
 * samples for each transition and a minimum OPEN dwell before a bite counts,
 * which kills landmark jitter / single-frame flicker.
 */
export class MouthFSM {
  open = false;
  closeSeq = 0;

  private readonly tOpen: number;
  private readonly tClose: number;
  private openCount = 0;
  private closeCount = 0;
  private openedAt = 0;

  constructor(tOpen: number, tClose: number) {
    this.tOpen = tOpen;
    this.tClose = tClose;
  }

  update(value: number, nowMs: number): void {
    if (!this.open) {
      this.openCount = value > this.tOpen ? this.openCount + 1 : 0;
      if (this.openCount >= 2) {
        this.open = true;
        this.openedAt = nowMs;
        this.openCount = 0;
        this.closeCount = 0;
      }
    } else {
      this.closeCount = value < this.tClose ? this.closeCount + 1 : 0;
      if (this.closeCount >= 2 && nowMs - this.openedAt >= 100) {
        this.open = false;
        this.closeSeq++;
        this.openCount = 0;
        this.closeCount = 0;
      }
    }
  }
}

export interface CalibResult {
  signal: Signal;
  tOpen: number;
  tClose: number;
}

/**
 * Collects jaw/lip ranges during the "open wide then close" calibration, then
 * picks whichever signal actually moved and derives open/close thresholds.
 */
export class Calibrator {
  private jawMin = Infinity;
  private jawMax = -Infinity;
  private lipMin = Infinity;
  private lipMax = -Infinity;
  private lastJaw: number | null = null;
  private lastLip: number | null = null;

  feed(jaw: number | null, lip: number | null): void {
    if (jaw !== null) {
      this.jawMin = Math.min(this.jawMin, jaw);
      this.jawMax = Math.max(this.jawMax, jaw);
      this.lastJaw = jaw;
    }
    if (lip !== null) {
      this.lipMin = Math.min(this.lipMin, lip);
      this.lipMax = Math.max(this.lipMax, lip);
      this.lastLip = lip;
    }
  }

  /** Live 0..1 gauge value: current signal normalized into its observed range. */
  get progress(): number {
    const jawRange = this.jawMax - this.jawMin;
    if (jawRange > 0.05 && this.lastJaw !== null && Number.isFinite(this.jawMin)) {
      return clamp01((this.lastJaw - this.jawMin) / jawRange);
    }
    const lipRange = this.lipMax - this.lipMin;
    if (lipRange > 0.005 && this.lastLip !== null && Number.isFinite(this.lipMin)) {
      return clamp01((this.lastLip - this.lipMin) / lipRange);
    }
    return 0;
  }

  finish(): CalibResult | null {
    const jawRange = this.jawMax - this.jawMin;
    if (jawRange > 0.15) {
      return { signal: "jaw", tOpen: this.jawMin + 0.55 * jawRange, tClose: this.jawMin + 0.3 * jawRange };
    }
    const lipRange = this.lipMax - this.lipMin;
    if (lipRange > 0.03) {
      return { signal: "lip", tOpen: this.lipMin + 0.55 * lipRange, tClose: this.lipMin + 0.3 * lipRange };
    }
    return null;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
